import {
  ArrowDownRightIcon,
  ArrowPathIcon,
  ArrowUpRightIcon,
  BuildingStorefrontIcon,
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  HandRaisedIcon,
  MapPinIcon,
  UsersIcon,
} from "@heroicons/react/24/solid";
import React, { useMemo, useState } from "react";
import { useQuery } from "react-query";
import { Link } from "react-router-dom";
import { apiConfig } from "../../config/apiConfig";
import { Chip, formatCurrency, formatLargeNumber } from "../../helpers/general";
import { useBookingQuery } from "../../hooks/bookings/useBookingQuery";
import { useCookie } from "../../hooks/useCookie";
import { useDriverQuery } from "../../hooks/drivers/useDriverQuery";
import { usePartnersQuery } from "../../hooks/Partners/usePartnersQuery";
import { usePlacesQuery } from "../../hooks/place/usePlacesQuery";
import { useRides } from "../../hooks/useRides";
import { useUserQuery } from "../../hooks/users/useUserQuery";
import apiService from "../../service/apiService";
import BookingTable from "../Bookings/BookingTable";
import { cardStyle } from "../Places/PlacesTable";
import RidersTable from "../taxi/RidersTable";
import {
  Delta,
  PeriodType,
  QuickFilter,
  UnpaidSummary,
} from "../../helpers/types";
import { BRAND } from "../../helpers/constants";
import { PERIODS } from "../../helpers/periods";
import { CarTaxiFront } from "lucide-react";

/**
 * Query params sent to useRides for each quick filter.
 * They match the options read by RidesService.findAllRides.
 */
const QUICK_FILTER_PARAMS: Record<
  Exclude<QuickFilter, null>,
  Record<string, unknown>
> = {
  bonus: { payed: false, isValidRide: true },
  coupon: { couponPaid: false },
  balance: { userBalancePaid: false },
};

const QUICK_FILTER_TITLES: Record<Exclude<QuickFilter, null>, string> = {
  bonus: "Bonus impayés",
  coupon: "Coupons impayés",
  balance: "Soldes utilisateur impayés",
};

const toYmd = (d: Date) => d.toISOString().split("T")[0];
const mad = (n: number) => `${formatCurrency(n)} MAD`;

/* ============================== data hooks ============================== */

/**
 * ADAPT: point this at your route for the unpaid/invalid export.
 * It must return { counts, amounts: { coupon, balance, bonus } }.
 */
function useUnpaidSummary() {
  const { token } = useCookie("vToken");
  return useQuery<UnpaidSummary>(
    ["adminHome", "unpaidSummary"],
    () => apiService.MakeGetRequest(apiConfig.rides.unpaidExport({}), token),
    { enabled: !!token, staleTime: 60_000, refetchOnWindowFocus: false },
  );
}

function useTaxiRecap(period: PeriodType, anchor: Date) {
  const { token } = useCookie("vToken");

  const filter = useMemo(() => {
    const Filter = PERIODS.find((p) => p.id === period)!.Filter;
    const f = new Filter();
    f.setValue(anchor);
    return f;
  }, [period, anchor]);

  const startDate = toYmd(filter.startDate);
  const endDate = toYmd(filter.endDate);

  const query = useQuery<TaxiStatistics>(
    ["adminHome", "taxiRecap", startDate, endDate],
    () =>
      apiService.MakeGetRequest(
        apiConfig.taxiStatistics.all({ startDate, endDate }),
        token,
      ),
    { enabled: !!token, keepPreviousData: true, staleTime: 60_000 },
  );

  return { filter, query };
}

/* ============================== small UI blocks ============================== */

const Skeleton = ({
  w = "100%",
  h = 16,
}: {
  w?: number | string;
  h?: number;
}) => (
  <div
    aria-hidden
    style={{
      width: w,
      height: h,
      borderRadius: 6,
      background: "#eceff3",
    }}
  />
);

function Section({
  title,
  subtitle,
  to,
  right,
  children,
  flush = false,
  className = "",
}: {
  title: string;
  subtitle?: string;
  to?: string;
  right?: React.ReactNode;
  children: React.ReactNode;
  flush?: boolean;
  className?: string;
}) {
  return (
    <div
      className={`d-flex flex-column ${className}`}
      style={{ ...cardStyle, padding: 0, overflow: "hidden" }}
    >
      <div
        className="d-flex flex-wrap justify-content-between align-items-center gap-2 px-3 py-3"
        style={{ borderBottom: "1px solid #f0f0f0" }}
      >
        <div>
          <h6 className="fw-bold mb-0" style={{ fontSize: 15 }}>
            {title}
          </h6>
          {subtitle && (
            <div className="text-muted" style={{ fontSize: 12 }}>
              {subtitle}
            </div>
          )}
        </div>
        <div className="d-flex align-items-center gap-2">
          {right}
          {to && (
            <Link
              to={to}
              className="btn btn-sm btn-outline-secondary rounded-pill px-3"
            >
              Voir tout
            </Link>
          )}
        </div>
      </div>
      <div className={flush ? "" : "p-3"} style={{ flex: 1, minWidth: 0 }}>
        {children}
      </div>
    </div>
  );
}

function InlineError({
  onRetry,
  label,
}: {
  onRetry?: () => void;
  label: string;
}) {
  return (
    <div className="d-flex flex-column align-items-center gap-2 py-4 text-center">
      <span className="text-muted" style={{ fontSize: 13 }}>
        {label}
      </span>
      {onRetry && (
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary rounded-pill px-3"
          onClick={onRetry}
        >
          <ArrowPathIcon width={14} className="me-1" />
          Réessayer
        </button>
      )}
    </div>
  );
}

const Empty = ({ text }: { text: string }) => (
  <p className="text-center text-muted py-5 mb-0">{text}</p>
);

/* ---------- platform totals ---------- */

function Total({
  to,
  label,
  value,
  icon: Icon,
  color,
}: {
  to: string;
  label: string;
  value?: number;
  icon: React.ElementType;
  color: string;
}) {
  return (
    <Link
      to={to}
      className="d-flex flex-column justify-content-between text-decoration-none h-100"
      style={{
        ...cardStyle,
        padding: 20,
        minHeight: 150,
        borderTop: `4px solid ${color}`,
      }}
    >
      <div className="d-flex align-items-start justify-content-between">
        <span
          className="d-inline-flex align-items-center justify-content-center"
          style={{
            width: 52,
            height: 52,
            borderRadius: 14,
            color,
            backgroundColor: `${color}22`,
          }}
        >
          <Icon width={28} />
        </span>
        <ArrowUpRightIcon width={16} style={{ color: "#9aa0a6" }} />
      </div>
      <div className="mt-3">
        <div
          className="fw-bold"
          style={{ fontSize: 34, lineHeight: 1.1, color: "#1f2937" }}
        >
          {value === undefined ? "—" : formatLargeNumber(value)}
        </div>
        <div
          className="text-truncate"
          style={{ fontSize: 14, fontWeight: 600, color: "#6b7280" }}
        >
          {label}
        </div>
      </div>
    </Link>
  );
}

/* ---------- recap ---------- */

function pctChange({ total, previousTotal }: Delta) {
  if (previousTotal === 0) return total > 0 ? 100 : 0;
  return ((total - previousTotal) / previousTotal) * 100;
}

function DeltaBadge({
  delta,
  periodName,
}: {
  delta: Delta;
  periodName?: string;
}) {
  const pct = pctChange(delta);
  const up = pct >= 0;
  const Arrow = up ? ArrowUpRightIcon : ArrowDownRightIcon;
  return (
    <span
      className="d-inline-flex align-items-center gap-1"
      style={{
        fontSize: 12,
        fontWeight: 600,
        color: up ? BRAND.green : BRAND.red,
      }}
    >
      <Arrow width={13} />
      {up ? "+" : ""}
      {pct.toFixed(1)}%
      {periodName && (
        <span className="text-muted fw-normal ms-1">
          vs {periodName.toLowerCase()} préc.
        </span>
      )}
    </span>
  );
}

function RecapStat({
  label,
  delta,
  money,
  accent,
  loading,
  periodName,
}: {
  label: string;
  delta?: Delta;
  money?: boolean;
  accent: string;
  loading: boolean;
  periodName?: string;
}) {
  return (
    <div className="col-6 col-xl-3">
      <div
        className="h-100 p-3"
        style={{
          border: "1px solid #eee",
          borderLeft: `4px solid ${accent}`,
          borderRadius: 12,
          background: "#fff",
        }}
      >
        <div
          className="text-muted mb-1"
          style={{ fontSize: 12, fontWeight: 600 }}
        >
          {label}
        </div>
        {loading || !delta ? (
          <div className="d-flex flex-column gap-2">
            <Skeleton w="70%" h={28} />
            <Skeleton w="50%" h={12} />
          </div>
        ) : (
          <>
            <div
              className="fw-bold text-dark"
              style={{ fontSize: 24, lineHeight: 1.15 }}
            >
              {money ? mad(delta.total) : formatLargeNumber(delta.total)}
            </div>
            <DeltaBadge delta={delta} periodName={periodName} />
          </>
        )}
      </div>
    </div>
  );
}

function Trend({ points }: { points: number[] }) {
  if (points.length < 2) {
    return (
      <div
        className="text-muted d-flex align-items-center justify-content-center"
        style={{ height: 90, fontSize: 12 }}
      >
        Pas assez de données pour la tendance
      </div>
    );
  }
  const W = 400;
  const H = 90;
  const pad = 6;
  const max = Math.max(...points, 1);
  const step = (W - pad * 2) / (points.length - 1);
  const coords = points.map((v, i) => [
    pad + i * step,
    H - pad - (v / max) * (H - pad * 2),
  ]);
  const line = coords
    .map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`)
    .join(" ");
  const area = `${pad},${H - pad} ${line} ${W - pad},${H - pad}`;
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      width="100%"
      height={90}
      role="img"
      aria-label="Tendance des revenus sur la période"
    >
      <polygon points={area} fill={BRAND.orange} opacity={0.12} />
      <polyline
        points={line}
        fill="none"
        stroke={BRAND.orange}
        strokeWidth={2.5}
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

function StatsRecap() {
  const [period, setPeriod] = useState<PeriodType>("week");
  const [anchor, setAnchor] = useState(() => new Date());
  const { filter, query } = useTaxiRecap(period, anchor);
  const data = query.data;
  const loading = query.isLoading;

  const derived = useMemo(() => {
    if (!data) return null;
    const { revenu, commission, rides } = data;
    return {
      avgFare: rides.total > 0 ? revenu.total / rides.total : 0,
      commissionRate:
        revenu.total > 0 ? (commission.total / revenu.total) * 100 : 0,
      net: revenu.total - commission.total,
    };
  }, [data]);

  const trend = useMemo(
    () =>
      Object.entries(data?.ridesBreakDown ?? {})
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([, v]) => v.revenue),
    [data],
  );

  const selectPeriod = (p: PeriodType) => {
    setPeriod(p);
    setAnchor(new Date());
  };
  const shift = (dir: "next" | "previous") =>
    setAnchor(
      dir === "next" ? filter.getNextPeriod() : filter.getPreviousPeriod(),
    );

  const controls = (
    <div className="d-flex flex-wrap align-items-center gap-2">
      <div className="btn-group btn-group-sm" role="group" aria-label="Période">
        {PERIODS.map((p) => (
          <button
            key={p.id}
            type="button"
            className={`btn ${period === p.id ? "btn-dark" : "btn-outline-secondary"}`}
            onClick={() => selectPeriod(p.id)}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className="d-flex align-items-center gap-1">
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          aria-label="Période précédente"
          onClick={() => shift("previous")}
        >
          <ChevronLeftIcon width={14} />
        </button>
        <span
          className="text-center fw-semibold"
          style={{ fontSize: 13, minWidth: 130 }}
        >
          {filter.getDisplayValue()}
        </span>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          aria-label="Période suivante"
          onClick={() => shift("next")}
        >
          <ChevronRightIcon width={14} />
        </button>
      </div>
    </div>
  );

  return (
    <Section
      title="Récapitulatif des statistiques"
      subtitle="Trajets complétés et valides"
      to="/statistics"
      right={controls}
    >
      {query.isError && !data ? (
        <InlineError
          label="Impossible de charger les statistiques."
          onRetry={() => query.refetch()}
        />
      ) : (
        <div
          style={{
            opacity: query.isFetching && data ? 0.6 : 1,
            transition: "opacity .15s",
          }}
        >
          <div className="row g-3">
            <RecapStat
              label="Revenu"
              delta={data?.revenu}
              money
              accent={BRAND.red}
              loading={loading}
              periodName={filter.name}
            />
            <RecapStat
              label="Commission"
              delta={data?.commission}
              money
              accent={BRAND.blue}
              loading={loading}
              periodName={filter.name}
            />
            <RecapStat
              label="Trajets"
              delta={data?.rides}
              accent={BRAND.orange}
              loading={loading}
              periodName={filter.name}
            />
            <RecapStat
              label="Nouveaux chauffeurs"
              delta={data?.drivers}
              accent={BRAND.green}
              loading={loading}
              periodName={filter.name}
            />
          </div>

          <div className="row g-3 mt-1">
            <div className="col-12 col-lg-7">
              <div
                className="text-muted mb-1"
                style={{ fontSize: 12, fontWeight: 600 }}
              >
                Revenu par jour
              </div>
              {loading ? <Skeleton h={90} /> : <Trend points={trend} />}
            </div>
            <div className="col-12 col-lg-5">
              <div className="row g-2 h-100">
                {[
                  {
                    label: "Panier moyen",
                    value: derived ? mad(derived.avgFare) : undefined,
                  },
                  {
                    label: "Taux de commission",
                    value: derived
                      ? `${derived.commissionRate.toFixed(1)} %`
                      : undefined,
                  },
                  {
                    label: "Revenu net chauffeurs",
                    value: derived ? mad(derived.net) : undefined,
                  },
                ].map((m) => (
                  <div key={m.label} className="col-12 col-sm-4 col-lg-12">
                    <div
                      className="d-flex justify-content-between align-items-center px-3 py-2 h-100"
                      style={{ background: "#f8f9fb", borderRadius: 10 }}
                    >
                      <span className="text-muted" style={{ fontSize: 12 }}>
                        {m.label}
                      </span>
                      {m.value === undefined ? (
                        <Skeleton w={70} h={14} />
                      ) : (
                        <span className="fw-bold" style={{ fontSize: 14 }}>
                          {m.value}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </Section>
  );
}

/* ---------- needs attention ---------- */

function TodoRow({
  label,
  count,
  amount,
  loading,
  to,
  onClick,
  active,
}: {
  label: string;
  count?: number;
  amount?: number;
  loading: boolean;
  to?: string;
  onClick?: () => void;
  active?: boolean;
}) {
  const has = (count ?? 0) > 0;
  const cls =
    "d-flex align-items-center justify-content-between gap-3 text-decoration-none p-3 w-100";
  const style: React.CSSProperties = {
    border: `1px solid ${active ? BRAND.orange : "#eee"}`,
    borderRadius: 12,
    background: active
      ? "rgba(240,129,63,0.07)"
      : has
        ? "rgba(220,53,69,0.04)"
        : "#fcfcfc",
    textAlign: "left",
  };

  const body = (
    <>
      <span style={{ minWidth: 0 }}>
        <span
          className="d-block fw-semibold text-dark"
          style={{ fontSize: 13 }}
        >
          {label}
        </span>
        {amount !== undefined && has && (
          <span className="d-block text-muted" style={{ fontSize: 12 }}>
            {mad(amount)} à régler
          </span>
        )}
      </span>
      {loading ? (
        <Skeleton w={48} h={22} />
      ) : (
        <Chip tone={has ? "danger" : "success"}>
          {count === undefined ? "—" : has ? count : "À jour"}
        </Chip>
      )}
    </>
  );

  return to ? (
    <Link to={to} className={cls} style={style}>
      {body}
    </Link>
  ) : (
    <button
      type="button"
      className={cls}
      style={style}
      onClick={onClick}
      disabled={!has && !active}
      aria-pressed={active}
    >
      {body}
    </button>
  );
}

/* ============================== page ============================== */

export default function AdminHome() {
  const [quick, setQuick] = useState<QuickFilter>(null);

  const { totalUsers } = useUserQuery();
  const { data: approvedDrivers } = useDriverQuery("APPROVED", 1, "DESC", true);
  const { data: pendingDrivers } = useDriverQuery("IN_REVIEW", 1, "DESC", true);
  const { totalPlaces } = usePlacesQuery();
  const { Partners } = usePartnersQuery();
  const { bookings, totalCount } = useBookingQuery();

  /**
   * ADAPT: useRides must accept page/limit + the filter params above
   * and return the paginated { items, meta } payload of findAllRides.
   */
  const { rides, isLoading: ridesLoading } = (useRides as any)({
    page: 1,
    limit: 5,
    ...(quick ? QUICK_FILTER_PARAMS[quick] : {}),
  }) as {
    rides?: { items?: any[]; meta?: { totalItems?: number } };
    isLoading?: boolean;
  };

  const unpaid = useUnpaidSummary();
  const counts = unpaid.data?.counts;
  const amounts = unpaid.data?.amounts;

  const recentBookings = useMemo(
    () => (bookings ?? []).slice(0, 5),
    [bookings],
  );
  const rideRows = useMemo(() => (rides?.items ?? []).slice(0, 5), [rides]);

  const pendingDriversCount = pendingDrivers?.meta?.totalItems as
    | number
    | undefined;
  const totalAttention =
    (counts?.bonus ?? 0) +
    (counts?.coupon ?? 0) +
    (counts?.balance ?? 0) +
    (pendingDriversCount ?? 0);

  const toggle = (f: Exclude<QuickFilter, null>) =>
    setQuick((c) => (c === f ? null : f));

  const today = new Date().toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  return (
    <section className="pb-4">
      {/* Header */}
      <div className="d-flex flex-wrap align-items-end justify-content-between gap-2 mb-4">
        <div>
          <h5 className="fs-4 fw-bold mb-0">Vue d'ensemble</h5>
          <div className="text-muted text-capitalize" style={{ fontSize: 13 }}>
            {today}
          </div>
        </div>
        {totalAttention > 0 && (
          <Chip tone="danger">
            {totalAttention} élément{totalAttention > 1 ? "s" : ""} à traiter
          </Chip>
        )}
      </div>

      {/* Platform totals */}
      <div className="mb-4">
        <div className="row g-3">
          {[
            {
              to: "/users",
              label: "Utilisateurs",
              value: totalUsers,
              icon: UsersIcon,
              color: BRAND.orange,
            },
            {
              to: "/places",
              label: "Établissements",
              value: totalPlaces,
              icon: BuildingStorefrontIcon,
              color: BRAND.blue,
            },
            {
              to: "/partners",
              label: "Partenaires",
              value: Partners?.length,
              icon: HandRaisedIcon,
              color: BRAND.green,
            },
            {
              to: "/bookings",
              label: "Réservations",
              value: totalCount,
              icon: CalendarDaysIcon,
              color: BRAND.red,
            },
            {
              to: "/drivers",
              label: "Chauffeurs actifs",
              value: approvedDrivers?.meta?.totalItems,
              icon: CarTaxiFront,
              color: BRAND.orange,
            },
            {
              to: "/rides",
              label: "Trajets",
              value: rides?.meta?.totalItems,
              icon: MapPinIcon,
              color: BRAND.blue,
            },
          ].map((t) => (
            <div key={t.to} className="col-12 col-sm-6 col-lg-4 col-xxl-2">
              <Total {...t} />
            </div>
          ))}
        </div>
      </div>

      {/* Statistics recap */}
      <div className="mb-4">
        <StatsRecap />
      </div>

      {/* Attention + bookings */}
      <div className="row g-3 mb-4">
        <div className="col-12 col-xl-4 d-flex">
          <Section
            className="w-100"
            title="À traiter"
            subtitle="Cliquez pour filtrer les trajets"
            right={
              quick ? (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary rounded-pill px-3"
                  onClick={() => setQuick(null)}
                >
                  Réinitialiser
                </button>
              ) : null
            }
          >
            {unpaid.isError ? (
              <InlineError
                label="Impossible de charger les paiements en attente."
                onRetry={() => unpaid.refetch()}
              />
            ) : (
              <div className="d-flex flex-column gap-2">
                <TodoRow
                  to="/drivers"
                  label="Chauffeurs en attente d'approbation"
                  count={pendingDriversCount}
                  loading={pendingDriversCount === undefined}
                />
                <TodoRow
                  label="Bonus de trajets impayés"
                  count={counts?.bonus}
                  amount={amounts?.bonus}
                  loading={unpaid.isLoading}
                  active={quick === "bonus"}
                  onClick={() => toggle("bonus")}
                />
                <TodoRow
                  label="Coupons impayés"
                  count={counts?.coupon}
                  amount={amounts?.coupon}
                  loading={unpaid.isLoading}
                  active={quick === "coupon"}
                  onClick={() => toggle("coupon")}
                />
                <TodoRow
                  label="Soldes utilisateur impayés"
                  count={counts?.balance}
                  amount={amounts?.balance}
                  loading={unpaid.isLoading}
                  active={quick === "balance"}
                  onClick={() => toggle("balance")}
                />
                {!unpaid.isLoading && amounts && (
                  <div
                    className="d-flex justify-content-between align-items-center px-3 pt-2 mt-1"
                    style={{ borderTop: "1px dashed #e3e3e3", fontSize: 13 }}
                  >
                    <span className="text-muted">Total à régler</span>
                    <span className="fw-bold">
                      {mad(amounts.bonus + amounts.coupon + amounts.balance)}
                    </span>
                  </div>
                )}
              </div>
            )}
          </Section>
        </div>

        <div className="col-12 col-xl-8 d-flex">
          <Section
            className="w-100"
            title="Dernières réservations"
            subtitle="Les 5 plus récentes"
            to="/bookings"
            flush
          >
            {recentBookings.length === 0 ? (
              <Empty text="Aucune réservation" />
            ) : (
              <BookingTable
                bookings={recentBookings}
                mode="list"
                dateField="createdAt"
              />
            )}
          </Section>
        </div>
      </div>

      {/* Rides */}
      <Section
        title={quick ? QUICK_FILTER_TITLES[quick] : "Derniers trajets"}
        subtitle={quick ? "Filtré par « À traiter »" : "Les 5 plus récents"}
        to="/rides"
        flush
      >
        {ridesLoading ? (
          <div className="d-flex flex-column gap-3 p-3">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} h={36} />
            ))}
          </div>
        ) : rideRows.length === 0 ? (
          <Empty
            text={quick ? "Aucun trajet pour ce filtre" : "Aucun trajet"}
          />
        ) : (
          <RidersTable riders={rideRows} showDriver={true} />
        )}
      </Section>
    </section>
  );
}
